import { lazy, Suspense, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PublicShell } from './layouts/PublicShell';
import { LandingPage } from './pages/LandingPage';
import { ContractPage } from './pages/ContractPage';
import { GroupInquiryPage } from './pages/GroupInquiryPage';
import { ServiceDetailPage } from './pages/ServiceDetailPage';
import { CoverageDetailPage } from './pages/CoverageDetailPage';
import { CaseDetailPage } from './pages/CaseDetailPage';
import { LawyerDetailPage } from './pages/LawyerDetailPage';
import { ChatPage } from './pages/ChatPage';
import { BlogPage } from './pages/BlogPage';
import { BlogSectionPage } from './pages/BlogSectionPage';
import { BlogPostPage } from './pages/BlogPostPage';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { coverageItems } from './data/coverage';
import { servicesData } from './data/services';
import { casesData } from './data/cases';
import { lawyersData } from './data/lawyers';
import { blogSections, blogPosts } from './data/blogPosts';
import { RuntimeErrorBoundary } from './components/FrontendErrorBoundary';

// 어드민 영역 — Tiptap 등 무겁고 브라우저 전용인 모듈을 포함한다.
// React.lazy 로 감싸 빌드 타임(SSG)의 라우트 import 에 끌려오지 않게 하고,
// vite.config.js 의 includedRoutes 에서 /admin/* 을 프리렌더 대상에서 제외한다.
// → SSG 중에는 절대 import·렌더되지 않고, 런타임에 SPA fallback 으로만 로드된다.
const AdminBlogListPage = lazy(() =>
  import('./pages/admin/AdminBlogListPage').then((m) => ({ default: m.AdminBlogListPage }))
);
const AdminBlogEditorPage = lazy(() =>
  import('./pages/admin/AdminBlogEditorPage').then((m) => ({ default: m.AdminBlogEditorPage }))
);
const AdminBlogAuthorsPage = lazy(() =>
  import('./pages/admin/AdminBlogAuthorsPage').then((m) => ({ default: m.AdminBlogAuthorsPage }))
);
const AdminChatbotPage = lazy(() =>
  import('./pages/admin/AdminChatbotPage').then((m) => ({ default: m.AdminChatbotPage }))
);
const AdminConsultPage = lazy(() =>
  import('./pages/admin/AdminConsultPage').then((m) => ({ default: m.AdminConsultPage }))
);

const lazyAdmin = (Component) => (
  <Suspense fallback={null}>
    <Component />
  </Suspense>
);

// 네트워크는 출시 전까지 ComingSoon 으로 노출.
//
// 블로그(미디어 센터)는 오픈됨. 본문은 백엔드 API 가 소스지만, 빌드 직전
// scripts/fetch-blog-data.js 가 발행글을 blogPosts.generated.json 스냅샷으로 떠서
// data/blogPosts.js 가 동기로 읽는다 → 다른 정적 페이지들과 똑같이 프리렌더된다.
// (브라우저 fetch 로 두면 크롤러가 빈 HTML 을 보고 sitemap 에서도 빠진다.)
//
// 대가: 글을 발행하면 재빌드·재배포해야 검색에 반영된다.

// 라우트 변경 시 스크롤 위치 보정. window/document 사용은 useEffect 안에서만 →
// 정적 렌더(renderToString) 시 실행되지 않아 SSR 안전.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) {
      try {
        const el = document.querySelector(hash);
        if (el) {
          el.scrollIntoView({ behavior: 'instant', block: 'start' });
          return;
        }
      } catch {
        // invalid selector (e.g. Figma capture hash) — ignore
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

// 전 라우트 공통 루트 레이아웃 — ScrollToTop 을 라우터 컨텍스트 안에 둔다.
function RootLayout() {
  return (
    <RuntimeErrorBoundary>
      <ScrollToTop />
      <Outlet />
    </RuntimeErrorBoundary>
  );
}

// react-router v7 객체 포맷의 라우트 배열. main.jsx 와 vite-react-ssg 가 공유한다.
export const routes = [
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        // pathless layout route — 공개 영역(Nav/Footer/ChatbotFab) 공통 셸
        element: <PublicShell />,
        children: [
          { index: true, element: <LandingPage /> },
          { path: 'contract', element: <ContractPage /> },
          { path: 'group-inquiry', element: <GroupInquiryPage /> },
          {
            path: 'services/:slug',
            element: <ServiceDetailPage />,
            getStaticPaths: () => servicesData.map((s) => `/services/${s.slug}`),
          },
          {
            path: 'coverage/:slug',
            element: <CoverageDetailPage />,
            getStaticPaths: () => coverageItems.map((c) => `/coverage/${c.slug}`),
          },
          {
            path: 'cases/:slug',
            element: <CaseDetailPage />,
            getStaticPaths: () => casesData.map((c) => `/cases/${c.slug}`),
          },
          {
            path: 'lawyer/:slug',
            element: <LawyerDetailPage />,
            getStaticPaths: () => lawyersData.map((l) => `/lawyer/${l.slug}`),
          },
          {
            path: 'network',
            element: (
              <ComingSoonPage
                title="네트워크 페이지를 준비하고 있습니다"
                description="저희와 함께하는 의료기관·전문가 네트워크 정보를 정리해 곧 공개할 예정입니다. 조금만 기다려 주세요."
              />
            ),
          },
          { path: 'chat', element: <ChatPage /> },
          { path: 'blog', element: <BlogPage /> },
          {
            path: 'blog/:section',
            element: <BlogSectionPage />,
            getStaticPaths: () => blogSections.map((s) => `/blog/${s.slug}`),
          },
          {
            path: 'blog/:section/:slug',
            element: <BlogPostPage />,
            getStaticPaths: () => blogPosts.map((p) => `/blog/${p.section}/${p.slug}`),
          },
          // catch-all — 위 어느 라우트도 매칭 안 되면 404. 반드시 마지막 자식.
          // 동적('*')이라 프리렌더 제외 → dist/404.html 없음. nginx SPA fallback
          // (/index.html)을 타고 클라이언트에서 NotFoundPage 가 그려진다(인프라 변경 0).
          { path: '*', element: <NotFoundPage /> },
        ],
      },
      // 어드민 — public 셸 없이 자체 레이아웃. lazy + 프리렌더 제외.
      { path: 'admin/blog', element: lazyAdmin(AdminBlogListPage) },
      { path: 'admin/blog/new', element: lazyAdmin(AdminBlogEditorPage) },
      { path: 'admin/blog/:id/edit', element: lazyAdmin(AdminBlogEditorPage) },
      { path: 'admin/blog/authors', element: lazyAdmin(AdminBlogAuthorsPage) },
      { path: 'admin/chatbot', element: lazyAdmin(AdminChatbotPage) },
      { path: 'admin/consult', element: lazyAdmin(AdminConsultPage) },
    ],
  },
];
